// Productivity's home: what's waiting on you, every table you can open (by workspace), and ways to start one:
// blank, from a template, or from an Excel or CSV file. Also the section of an area's home that lists the tables
// tagged to that area.
import { useMemo, useRef, useState, type DragEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { FieldType, TableListItem, Template } from "./pApi";
import { pApi } from "./pApi";
import { useLoad } from "../hooks";
import { Chat } from "../Chat";
import { SiteHeader } from "../areaSite";
import { ago } from "../ui";
import { AREA_NAMES, TYPE_INFO } from "./format";
import { VIEW_ICON } from "./TablePage";
import { readTable } from "../cadfile";
import { findArea } from "../suite";

function TableCard({ t, area }: { t: TableListItem; area?: string }) {
  return (
    <Link to={`/p/${t.id}${area ? `?a=${area}` : ""}`} className="pv-tcard">
      <span className="pv-tcard-n">{t.name}</span>
      {t.description && <span className="pv-tcard-d">{t.description}</span>}
      <span className="pv-tcard-m">
        <span aria-label={`Views: ${t.views.join(", ")}`}>{t.views.map((v) => <span key={v} className="pv-vi" title={v}>{VIEW_ICON[v]}</span>)}</span>
        <span>{t.rows.toLocaleString()} {t.rows === 1 ? "record" : "records"}</span>
        <span>changed {ago(t.updatedAt)}</span>
        {t.access !== "edit" && <span>{t.access === "private" ? "Private" : "View only for most"}</span>}
      </span>
      {!area && t.areas.length > 0 && <span className="pv-tags">{t.areas.map((a) => <span key={a} className="pv-tag">{AREA_NAMES[a] ?? a}</span>)}</span>}
    </Link>
  );
}

function NewBlank({ from, label = "Blank table" }: { from: string; label?: string }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const make = async () => {
    if (!name.trim()) return;
    try { const r = await pApi.createTable({ name: name.trim(), from }); nav(`/p/${r.id}${from !== "productivity" ? `?a=${from}` : ""}`); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  if (!open) return <button className="pv-start" onClick={() => setOpen(true)}><span className="pv-start-i">+</span><span><strong>{label}</strong><span className="muted">Name it and start typing</span></span></button>;
  return (
    <div className="pv-start on">
      <input className="input" autoFocus placeholder="Tooling tracker, Line 4 install…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") make(); if (e.key === "Escape") setOpen(false); }} />
      <div className="btn-row"><button className="btn primary" disabled={!name.trim()} onClick={make}>Create</button><button className="btn" onClick={() => setOpen(false)}>Cancel</button></div>
      {err && <div className="msg error">{err}</div>}
      {from === "productivity" && <span className="hint">It's placed in the area its name suggests (a supplier tracker shows in Supply chain too). Change that any time.</span>}
    </div>
  );
}

export function ProductivityHome() {
  const [q, setQ] = useState("");
  const { data: tables, error, reload } = useLoad(() => pApi.tables(), []);
  const { data: me } = useLoad(() => pApi.me(), []);
  const { data: tpl } = useLoad(() => pApi.templates(), []);
  const groups = useMemo(() => {
    const t = (tables ?? []).filter((x) => !q.trim() || `${x.name} ${x.description} ${x.workspace ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()));
    const by = new Map<string, TableListItem[]>();
    for (const x of t) { const k = x.workspace ?? ""; (by.get(k) ?? by.set(k, []).get(k)!).push(x); }
    return [...by.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  }, [tables, q]);
  const waiting = (me?.approvals.length ?? 0) + (me?.mentions.length ?? 0) + (me?.assigned.length ?? 0);

  return (
    <div className="page area-home area-site pv-home">
      <SiteHeader d={findArea("productivity")!} summary="Tables, project plans, boards, calendars and forms your teams build themselves, next to the rest of courageMFG. In place of Smartsheet, Airtable and the trackers in Excel.">
        <Chat area="productivity" compact placeholder="Ask Courage Chat to build a tracker, or about any table" onChanged={() => reload()} />
      </SiteHeader>

      <div className="pv-starts">
        <NewBlank from="productivity" />
        <Link className="pv-start" to="/productivity/templates"><span className="pv-start-i">▤</span><span><strong>From a template</strong><span className="muted">Project plan, NPI launch, tooling, kaizen…</span></span></Link>
        <Link className="pv-start" to="/productivity/import"><span className="pv-start-i">⇪</span><span><strong>Import Excel or CSV</strong><span className="muted">Each column gets the right type</span></span></Link>
      </div>

      {waiting > 0 && me && (
        <section className="panel" aria-labelledby="wait-h">
          <div className="panel-h"><h2 id="wait-h" className="ph">Waiting on you</h2></div>
          <ul className="rec-list">
            {me.approvals.map((a) => (
              <li key={`a${a.row.id}${a.field.key}`}>
                <Link to={`/p/${a.table.id}?r=${a.row.id}`} className="rl-main"><span className="rl-t">Approve: {a.row.label}</span><span className="rl-m">{a.table.name} · {a.field.name} · asked by {a.approval.requestedByName ?? "someone"}{a.approval.note ? ` · “${a.approval.note}”` : ""}</span></Link>
                <span className="rl-side"><span className="status warn">Waiting for you</span><span className="rl-m">{ago(a.approval.at)}</span></span>
              </li>
            ))}
            {me.mentions.slice(0, 12).map((m) => (
              <li key={m.id}>
                <Link to={`/p/${m.table.id}?r=${m.row.id}`} className="rl-main"><span className="rl-t">{m.kind === "comment" ? `${m.by ?? "Someone"} mentioned you` : m.kind === "approval" ? (m.to === "pending" ? `${m.by ?? "Someone"} asked you to approve` : `${m.by ?? "Someone"} ${m.to === "approved" ? "approved" : "rejected"}`) : m.body ?? "Notification"}: {m.row.label}</span><span className="rl-m">{m.table.name}{m.kind === "comment" && m.body ? ` · “${m.body.slice(0, 120)}”` : ""}</span></Link>
                <span className="rl-side"><span className="rl-m">{ago(m.at)}</span></span>
              </li>
            ))}
            {me.assigned.slice(0, 12).map((a) => (
              <li key={`s${a.row.id}`}>
                <Link to={`/p/${a.table.id}?r=${a.row.id}`} className="rl-main"><span className="rl-t">{a.row.label}</span><span className="rl-m">{a.table.name} · you're the {a.field}</span></Link>
                <span className="rl-side"><span className="rl-m">changed {ago(a.updatedAt)}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="tables-h">
        <div className="sec-h"><h2 id="tables-h" style={{ margin: 0, fontSize: 16 }}>Tables and plans</h2><span className="muted">{tables ? `${tables.length} you can open` : ""}</span>
          <input className="input" style={{ maxWidth: 280, marginLeft: "auto" }} type="search" placeholder="Find a table" aria-label="Find a table" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {error ? <div className="msg error">{(error as Error).message}</div> : !tables ? <div className="empty">Loading…</div> : !tables.length ? (
          <div className="panel empty">No tables yet. Start one above, or ask Courage Chat: “make a tracker for our gauges with due dates and owners”.</div>
        ) : groups.map(([ws, list]) => (
          <div key={ws || "-"} className="pv-ws">
            {(groups.length > 1 || ws) && <h3 className="pv-ws-h">{ws || "Not in a workspace"}</h3>}
            <div className="pv-tcards">{list.map((t) => <TableCard key={t.id} t={t} />)}</div>
          </div>
        ))}
      </section>

      {tpl && (
        <section aria-labelledby="tpl-h" style={{ marginTop: 24 }}>
          <div className="sec-h"><h2 id="tpl-h" style={{ margin: 0, fontSize: 16 }}>Start from a template</h2><Link to="/productivity/templates">All templates</Link></div>
          <div className="pv-tcards">{tpl.templates.slice(0, 4).map((t) => <TemplateCard key={t.id} t={t} />)}</div>
        </section>
      )}
    </div>
  );
}

function TemplateCard({ t, area }: { t: Template; area?: string | null }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const use = async () => {
    setBusy(true);
    try { const r = await pApi.createTable({ template: t.id, ...(area ? { from: area } : {}) }); nav(`/p/${r.id}${area ? `?a=${area}` : ""}`); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  return (
    <div className="pv-tcard pv-tpl">
      <span className="pv-tcard-n">{t.name}</span>
      <span className="pv-tcard-d">{t.summary}</span>
      <span className="pv-tcard-m"><span>{t.views.map((v) => <span key={v.name} className="pv-vi" title={v.name}>{VIEW_ICON[v.type]}</span>)}</span><span>{t.fields.length} fields</span>{t.starterRows ? <span>{t.starterRows} starter tasks</span> : null}</span>
      <span className="muted" style={{ fontSize: 12 }}>Instead of {t.replaces.charAt(0).toLowerCase() + t.replaces.slice(1)}</span>
      {err && <span className="msg error">{err}</span>}
      <button className="btn" disabled={busy} onClick={use}>{busy ? "Making it…" : "Use this template"}</button>
    </div>
  );
}

export function TemplatesPage() {
  const [params] = useSearchParams();
  const area = params.get("a");
  const a = area ? findArea(area) : null;
  const { data } = useLoad(() => pApi.templates(), []);
  return (
    <div className="page pv-home">
      <nav className="crumbs"><Link to="/">Home</Link><span>/</span>{a ? <Link to={`/a/${a.id}`}>{a.name}</Link> : <Link to="/a/productivity">Productivity</Link>}<span>/</span><span>Templates</span></nav>
      <div className="page-title"><h1>Templates</h1><span className="sub">Starter tables for manufacturers. Each comes with its fields and views; change anything after.{a ? ` It will show in ${a.name}.` : ""}</span></div>
      {!data ? <div className="empty">Loading…</div> : (
        <div className="pv-tcards wide">
          {data.templates.map((t) => (
            <div key={t.id} className="pv-tpl-wrap">
              <TemplateCard t={t} area={area} />
              <div className="pv-tpl-fields">{t.fields.map((f) => <span key={f.name} className="pv-chip"><span className="pg-ti">{TYPE_INFO[f.type].icon}</span>{f.name}</span>)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- import ----------
const IMPORT_TYPES: FieldType[] = ["text", "longtext", "number", "currency", "percent", "date", "checkbox", "select", "multiselect", "person", "url", "email"];
export function ImportPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const area = params.get("a");
  const a = area ? findArea(area) : null;
  const [file, setFile] = useState<string | null>(null);
  const [table, setTable] = useState<string[][] | null>(null);
  const [headerRow, setHeaderRow] = useState(true);
  const [name, setName] = useState("");
  const [workspace, setWorkspace] = useState("");
  const [types, setTypes] = useState<(FieldType | null)[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const load = async (f: File) => {
    setErr(null);
    try {
      const t = await readTable(f);
      if (!t.length) throw new Error("That file has no rows.");
      setTable(t); setFile(f.name); setName(f.name.replace(/\.(xlsx|xlsm|csv|txt|tsv)$/i, "").replace(/[_]+/g, " ")); setTypes(new Array(Math.max(...t.map((r) => r.length))).fill(null));
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const width = table ? Math.max(...table.map((r) => r.length)) : 0;
  const header = table ? (headerRow ? Array.from({ length: width }, (_, i) => table[0][i] ?? "") : Array.from({ length: width }, (_, i) => `Column ${i + 1}`)) : [];
  const body = table ? (headerRow ? table.slice(1) : table) : [];
  const go = async () => {
    if (!table) return;
    setBusy(true); setErr(null);
    try {
      const r = await pApi.importTable({ name: name.trim() || file || "Imported", workspace: workspace.trim() || null, ...(area ? { from: area } : {}), header, rows: body, types });
      nav(`/p/${r.id}${area ? `?a=${area}` : ""}`);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) load(f); };
  return (
    <div className="page pv-home">
      <nav className="crumbs"><Link to="/">Home</Link><span>/</span>{a ? <Link to={`/a/${a.id}`}>{a.name}</Link> : <Link to="/a/productivity">Productivity</Link>}<span>/</span><span>Import</span></nav>
      <div className="page-title"><h1>Import a spreadsheet</h1><span className="sub">An Excel (.xlsx) or CSV file becomes a table. Each column gets a type from its values (numbers, dates, choices, yes/no…); change any before importing.</span></div>
      {err && <div className="msg error" role="alert">{err}</div>}
      {!table ? (
        <div className={`pv-drop${over ? " over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
          <input ref={input} type="file" accept=".xlsx,.xlsm,.csv,.txt,.tsv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) load(f); e.target.value = ""; }} />
          <span>Drop an Excel or CSV file here, or <button className="linkish" onClick={() => input.current?.click()}>choose a file</button>.</span>
          <span className="muted" style={{ fontSize: 12 }}>The first sheet is imported. Up to 20,000 rows.</span>
        </div>
      ) : (
        <>
          <div className="pv-imp-opts">
            <div className="field"><label htmlFor="im-n">Table name</label><input id="im-n" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="field"><label htmlFor="im-w">Workspace <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label><input id="im-w" className="input" value={workspace} onChange={(e) => setWorkspace(e.target.value)} /></div>
            <label className="pv-cb"><input type="checkbox" checked={headerRow} onChange={(e) => setHeaderRow(e.target.checked)} /> The first row has the column names</label>
          </div>
          <p className="muted">{body.length.toLocaleString()} rows from {file}. First {Math.min(8, body.length)} shown.</p>
          <div className="table-wrap panel">
            <table className="dgrid compact">
              <thead>
                <tr>{header.map((h, i) => <th key={i}>{h || `Column ${i + 1}`}</th>)}</tr>
                <tr>{header.map((_, i) => (
                  <th key={i}><select className="select" aria-label={`Type of ${header[i]}`} value={types[i] ?? ""} onChange={(e) => setTypes(types.map((t, j) => (j === i ? ((e.target.value || null) as FieldType | null) : t)))}>
                    <option value="">Guess from the values</option>{IMPORT_TYPES.map((t) => <option key={t} value={t}>{TYPE_INFO[t].label}</option>)}
                  </select></th>
                ))}</tr>
              </thead>
              <tbody>{body.slice(0, 8).map((r, i) => <tr key={i}>{header.map((_, j) => <td key={j}>{r[j] ?? ""}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <div className="btn-row" style={{ marginTop: 16 }}>
            <button className="btn primary" disabled={busy || !body.length} onClick={go}>{busy ? "Importing…" : `Import ${body.length.toLocaleString()} rows`}</button>
            <button className="btn" onClick={() => setTable(null)}>Choose another file</button>
          </div>
        </>
      )}
    </div>
  );
}

/** On an area's home: the tables tagged to it, and ways to start one there. */
export function AreaTables({ area, name }: { area: string; name: string }) {
  const { data } = useLoad(() => pApi.tables({ area }), [area]);
  return (
    <section aria-labelledby="pt-h" className="pv-area-sec">
      <div className="sec-h">
        <h2 id="pt-h" style={{ margin: 0, fontSize: 16 }}>Tables and plans</h2>
        <span className="muted">Trackers, plans and boards tagged to {name}</span>
        <span className="pv-area-acts"><Link to={`/productivity/templates?a=${area}`}>From a template</Link><Link to={`/productivity/import?a=${area}`}>Import</Link></span>
      </div>
      <div className="pv-tcards">
        {(data ?? []).map((t) => <TableCard key={t.id} t={t} area={area} />)}
        <NewBlank from={area} label="New table here" />
      </div>
    </section>
  );
}
